/**
 * PhoneVerificationPrompt - Prompts existing subscribed users to verify their phone number
 *
 * Shown when:
 * - User has a valid subscription (ACTIVE or TRIALING)
 * - User's phone is NOT verified (phone_verified !== true)
 * - User has NOT dismissed this prompt this session
 *
 * Features:
 * - Pre-fills existing phone number from profile
 * - Country code selector (react-phone-number-input)
 * - OTP verification flow (6-digit code)
 * - Skippable at any step (skip stores session flag)
 * - Error handling with skip fallback
 * - Resend countdown timer
 */

import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import { requestPhoneOTP, verifyPhoneOTP } from '@/services/customerService'
import type { User } from '@/features/auth/types'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

interface PhoneVerificationPromptProps {
  user: User
  onComplete: () => void
  onSkip: () => void
}

type PromptStep = 'phone_entry' | 'code_entry'

const SESSION_DISMISS_KEY = 'phone_verify_prompt_dismissed'

/** Check if user has dismissed the prompt this session */
export function hasBeenDismissedThisSession(): boolean {
  return sessionStorage.getItem(SESSION_DISMISS_KEY) === 'true'
}

/** Mark prompt as dismissed for this session */
export function dismissForSession(): void {
  sessionStorage.setItem(SESSION_DISMISS_KEY, 'true')
}

export function PhoneVerificationPrompt({ user, onComplete, onSkip }: PhoneVerificationPromptProps) {
  const [step, setStep] = useState<PromptStep>('phone_entry')
  const [phone, setPhone] = useState(user.phone_number || '')
  const [verificationCode, setVerificationCode] = useState('')
  const [maskedPhone, setMaskedPhone] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Resend countdown
  const [canResend, setCanResend] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(30)

  useEffect(() => {
    if (step === 'code_entry' && !canResend && resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(prev => prev - 1), 1000)
      return () => clearTimeout(timer)
    } else if (resendCountdown === 0) {
      setCanResend(true)
    }
  }, [step, canResend, resendCountdown])

  const isPhoneValid = phone.trim() && isValidPhoneNumber(phone)

  function handleSkip() {
    dismissForSession()
    onSkip()
  }

  async function handleSendCode() {
    if (!isPhoneValid) {
      setError('Please enter a valid phone number')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const result = await requestPhoneOTP(phone.trim())
      setMaskedPhone(result.maskedPhone || phone)
      setStep('code_entry')
      setCanResend(false)
      setResendCountdown(30)
      setVerificationCode('')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send code'
      setError(`${msg} — You can skip and try again later.`)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleVerifyCode() {
    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit code')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await verifyPhoneOTP(phone.trim(), verificationCode, user.id)
      dismissForSession()
      onComplete()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleResend() {
    if (!canResend) return

    setIsSubmitting(true)
    setError(null)

    try {
      const result = await requestPhoneOTP(phone.trim())
      setMaskedPhone(result.maskedPhone || phone)
      setCanResend(false)
      setResendCountdown(30)
      setVerificationCode('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={handleSkip}
      showCloseButton={true}
      maxWidth="max-w-md"
    >
      <div className="space-y-5">
        {/* === STEP 1: Phone Entry === */}
        {step === 'phone_entry' && (
          <>
            <div className="text-center">
              <div className="mx-auto w-14 h-14 bg-braydin-sand2 rounded-full flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-braydin-olive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Verify Your Phone Number
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Get notified by SMS when you receive messages and updates. Verify your number to enable notifications.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <PhoneInput
                international
                defaultCountry="US"
                value={phone}
                onChange={(value) => {
                  setPhone(value || '')
                  setError(null)
                }}
                className="phone-input-custom"
                placeholder="Enter phone number"
              />
              {phone.trim() && !isPhoneValid && (
                <p className="mt-1 text-sm text-red-500">Please enter a valid phone number</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                We'll send a verification code to confirm your number.
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              onClick={handleSendCode}
              disabled={!isPhoneValid || isSubmitting}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                isPhoneValid && !isSubmitting
                  ? 'bg-braydin-olive hover:bg-braydin-oliveDark text-white cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Verification Code'
              )}
            </button>

            <button
              onClick={handleSkip}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              Remind me later
            </button>
          </>
        )}

        {/* === STEP 2: Code Entry === */}
        {step === 'code_entry' && (
          <>
            <div className="text-center">
              <div className="mx-auto w-14 h-14 bg-braydin-sand2 rounded-full flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-braydin-olive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Enter Verification Code</h2>
              <p className="mt-2 text-sm text-gray-600">
                We sent a 6-digit code to
              </p>
              <p className="mt-1 text-base font-semibold text-gray-900">{maskedPhone}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                6-Digit Code
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/\D/g, ''))
                  setError(null)
                }}
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-braydin-olive focus:border-transparent"
                placeholder="000000"
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              onClick={handleVerifyCode}
              disabled={verificationCode.length !== 6 || isSubmitting}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                verificationCode.length === 6 && !isSubmitting
                  ? 'bg-braydin-olive hover:bg-braydin-oliveDark text-white cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Verify'
              )}
            </button>

            {/* Resend */}
            <div className="text-center">
              {canResend ? (
                <button
                  onClick={handleResend}
                  disabled={isSubmitting}
                  className="text-sm text-braydin-olive hover:text-braydin-oliveDark font-medium disabled:text-gray-400"
                >
                  Resend Code
                </button>
              ) : (
                <p className="text-sm text-gray-500">Resend code in {resendCountdown}s</p>
              )}
            </div>

            {/* Change number / Skip */}
            <div className="flex justify-between items-center pt-3 border-t border-gray-200">
              <button
                onClick={() => { setStep('phone_entry'); setVerificationCode(''); setError(null) }}
                disabled={isSubmitting}
                className="text-sm text-gray-600 hover:text-gray-800 font-medium disabled:text-gray-400"
              >
                Change number
              </button>
              <button
                onClick={handleSkip}
                disabled={isSubmitting}
                className="text-sm text-gray-500 hover:text-gray-700 font-medium disabled:text-gray-400"
              >
                Skip for now
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
